/**
 * Word Insert/Delete Operations
 *
 * Handles paragraph-level INSERT and DELETE operations for auto-redline.
 * Extracted from wordBatchEditOperations.ts to follow Single Responsibility Principle.
 *
 * CS-259: Supports index adjustment for out-of-order operation application.
 */

/* global Word */

import { EditOperation, EditApplicationResult } from "../../../interfaces/autoRedlineTypes";
import { createLogger } from "../../../utils/logger";
import { ParagraphApplyContext } from "./applyContext";
import { matchesExpectedParagraph } from "./paragraphIdentityGuard";
import { applyListChange } from "./wordListStructureOperations";
import { resolveListChange } from "./listChangeHelpers";

const log = createLogger("WordInsertDeleteOperations");

/** Result from paragraph operation that can continue or return early */
export interface ParagraphOperationResult {
  /** Whether to continue processing (false = early return with result) */
  shouldContinue: boolean;
  /** The result if shouldContinue is false */
  result?: EditApplicationResult;
}

/**
 * Apply an INSERT paragraph operation
 *
 * Creates a new paragraph before or after the specified paragraphId.
 * Applies the edit's structured list change (listChange) to the new paragraph,
 * best-effort — use newList or continueList on inserts.
 *
 * @param edit - The edit operation containing position and replacementText
 * @param context - Word request context
 * @param startTime - Operation start time for duration tracking
 * @param paragraphId - The paragraph index from AutoRedlineOutput (0-based)
 * @param apply - Shared per-apply context (tracker, track-changes mode, snapshot)
 * @returns Promise of operation result
 */
export async function applyInsertOperation(
  edit: EditOperation,
  context: Word.RequestContext,
  startTime: number,
  paragraphId: number,
  apply: ParagraphApplyContext = {}
): Promise<EditApplicationResult> {
  const { indexTracker, trackChangesMode } = apply;

  log.debug(`INSERT operation starting for ${edit.editId}`, {
    position: edit.position,
    paragraphId,
    replacementTextLength: edit.replacementText.length,
    listChange: edit.listChange,
    trackChangesMode,
    hasIndexTracker: !!indexTracker,
  });

  // Validate position is before or after
  if (edit.position !== "before" && edit.position !== "after") {
    const error = `INSERT operation requires position='before' or 'after', got '${edit.position}'`;
    log.error(`Edit ${edit.editId} failed: ${error}`);
    return {
      success: false,
      editId: edit.editId,
      error,
      duration: Date.now() - startTime,
    };
  }

  // Calculate adjusted index based on previously applied insertions
  const originalIndex = paragraphId;
  const adjustedIndex = indexTracker
    ? indexTracker.calculateAdjustedIndex(originalIndex, edit.position)
    : originalIndex;

  if (adjustedIndex !== originalIndex) {
    log.debug(`Index adjusted for ${edit.editId}: ${originalIndex} → ${adjustedIndex}`, {
      position: edit.position,
    });
  }

  // Get all paragraphs
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  // Validate adjusted paragraph_id is within bounds. The lower bound is NOT
  // defensive here: a track-changes-off DELETE at index N drives INSERT-AFTER
  // adjustments for indices >= N down by one, so deleting p0 and then inserting
  // after p0 resolves to -1. items[-1] is undefined, and calling .load() on it
  // threw out of this un-try/caught loop and failed every edit in the output.
  if (adjustedIndex < 0 || adjustedIndex >= paragraphs.items.length) {
    const error = `paragraph_id ${originalIndex} (adjusted: ${adjustedIndex}) out of bounds (${paragraphs.items.length} paragraphs)`;
    log.error(`Edit ${edit.editId} failed: ${error}`);
    return {
      success: false,
      editId: edit.editId,
      error,
      duration: Date.now() - startTime,
    };
  }

  const targetParagraph = paragraphs.items[adjustedIndex];
  targetParagraph.load("isListItem");
  await context.sync();

  // HYBRID APPROACH: Create paragraph structure first, then insert tracked text
  // Step 1: Create empty paragraph using insertParagraph (creates proper paragraph structure)
  const insertLoc =
    edit.position === "before" ? Word.InsertLocation.before : Word.InsertLocation.after;
  const newParagraph = targetParagraph.insertParagraph("", insertLoc);
  newParagraph.load("isListItem");
  await context.sync();

  // Record the insertion the moment the paragraph is committed, NOT after the
  // steps below. From this sync onward the document holds one extra paragraph;
  // if the text insertion or the list change then throws, a tracker that had not
  // yet recorded it would be one short, and the next INSERT in the same run
  // would resolve to the wrong index and land against the wrong paragraph.
  // Recording here also keeps it ahead of the list change, whose `continueList`
  // anchor is resolved by index and must account for this paragraph.
  if (indexTracker) {
    indexTracker.recordInsertion(originalIndex, edit.position, trackChangesMode);
  }

  // Step 2: Insert actual text using insertText (this will be tracked by track changes)
  newParagraph.insertText(edit.replacementText, Word.InsertLocation.start);
  // Re-load isListItem since text insertion may have changed list membership state
  newParagraph.load("isListItem");
  await context.sync();

  log.debug(`Paragraph state after text insertion for ${edit.editId}`, {
    isListItem: newParagraph.isListItem,
    listChange: edit.listChange,
  });

  // Step 3: Apply the structured list change to the new paragraph (best-effort).
  // The paragraph insert already succeeded, so list-format failures must not cause
  // the whole operation to report as failed. Inserts use newList / continueList.
  const listChange = resolveListChange(edit);
  if (listChange) {
    try {
      const result = await applyListChange(newParagraph, listChange, context, indexTracker);
      if (!result.success) {
        log.warn(`List change failed for ${edit.editId} (insert still succeeded): ${result.error}`);
      }
    } catch (listError) {
      const errorMsg = listError instanceof Error ? listError.message : String(listError);
      log.warn(`List change threw for ${edit.editId} (insert still succeeded): ${errorMsg}`);
    }
  }

  log.info(`Insert operation completed for ${edit.editId}`, {
    position: edit.position,
    paragraph_id: originalIndex,
    adjustedIndex,
    duration: Date.now() - startTime,
  });

  return {
    success: true,
    editId: edit.editId,
    duration: Date.now() - startTime,
  };
}

/**
 * Apply a DELETE paragraph operation
 *
 * Removes the paragraph at the specified paragraphId.
 * Deletion is tracked by Word's track changes feature.
 *
 * @param edit - The edit operation
 * @param context - Word request context
 * @param startTime - Operation start time for duration tracking
 * @param paragraphId - The paragraph index from AutoRedlineOutput (0-based)
 * @param apply - Shared per-apply context (tracker, track-changes mode, snapshot)
 * @returns Promise of operation result
 */
export async function applyDeleteOperation(
  edit: EditOperation,
  context: Word.RequestContext,
  startTime: number,
  paragraphId: number,
  apply: ParagraphApplyContext = {}
): Promise<EditApplicationResult> {
  const { indexTracker, trackChangesMode } = apply;

  log.debug(`DELETE operation starting for ${edit.editId}`, {
    paragraphId,
    trackChangesMode,
    hasIndexTracker: !!indexTracker,
  });

  // Calculate adjusted index based on previously applied operations
  const originalIndex = paragraphId;
  const adjustedIndex = indexTracker
    ? indexTracker.calculateAdjustedIndexForDelete(originalIndex)
    : originalIndex;

  // Get all paragraphs
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  // Validate adjusted paragraph_id is within bounds. The lower bound is
  // defensive — delete adjustments floor at 0 today — but it costs nothing and
  // keeps the three resolution paths saying the same thing.
  if (adjustedIndex < 0 || adjustedIndex >= paragraphs.items.length) {
    const error = `paragraph_id ${originalIndex} (adjusted: ${adjustedIndex}) out of bounds (${paragraphs.items.length} paragraphs)`;
    log.error(`Edit ${edit.editId} failed: ${error}`);
    return {
      success: false,
      editId: edit.editId,
      error,
      duration: Date.now() - startTime,
    };
  }

  const targetParagraph = paragraphs.items[adjustedIndex];
  targetParagraph.load("text");
  await context.sync();

  // Verify we are about to delete the paragraph the preview was built from —
  // ALWAYS, not only when a sibling edit touched it. Deletion is irreversible in
  // practice and, with track changes off, leaves no revision mark: nothing else
  // in this path ever compares the target's text, so an index that has drifted
  // silently destroys the wrong paragraph.
  //
  // Uses the same predicate as the preview's stale guard, so the two cannot
  // disagree — including its self-attribution branch, without which a legitimate
  // sibling EDIT + DELETE on the same paragraph would refuse here.
  const verdict = matchesExpectedParagraph({
    expectedText: apply.expectedParagraphText,
    liveText: targetParagraph.text,
    operationType: "delete",
    messageId: apply.messageId,
    paragraphId: originalIndex,
  });

  if (!verdict.matched) {
    // Lengths only — paragraph text is contract content and never goes to logs.
    log.error(`DELETE refused for ${edit.editId}: target does not match the preview`, {
      paragraphId: originalIndex,
      adjustedIndex,
      reason: verdict.reason,
      expectedLength: (apply.expectedParagraphText ?? "").length,
      liveLength: targetParagraph.text.length,
    });
    return {
      success: false,
      editId: edit.editId,
      error:
        "The paragraph to delete no longer matches the preview, so it was left unchanged. " +
        "Ask Contract Space to redo the edits.",
      duration: Date.now() - startTime,
    };
  }

  log.debug(`Deleting paragraph #${originalIndex} (adjusted: ${adjustedIndex})`, {
    selfAttributed: verdict.selfAttributed,
  });

  // Delete the paragraph (tracked by track changes)
  targetParagraph.delete();
  await context.sync();

  // Record successful deletion for future index adjustments
  if (indexTracker) {
    indexTracker.recordDeletion(originalIndex, trackChangesMode);
  }

  log.info(`Delete operation completed for ${edit.editId}`, {
    paragraph_id: originalIndex,
    adjustedIndex,
    duration: Date.now() - startTime,
  });

  return {
    success: true,
    editId: edit.editId,
    duration: Date.now() - startTime,
  };
}
